import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, Check } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui";
import { apiFetch } from "@/lib/api/client";
import { RUBROS } from "@/features/onboarding/industries";
import { keys } from "@/lib/api/keys";
import { cn } from "@/lib/utils";

/**
 * Los rubros, con el nombre que usaría quien los elige.
 *
 * `real_estate` va primero y aparte: es el único que hoy cambia la aplicación
 * —habilita Propiedades, las visitas y los campos de búsqueda—, y es el negocio
 * del que viene todo lo construido. El resto está para que el que entra se
 * reconozca en la lista; internamente se comportan igual.
 */
export interface BusinessProfile {
  industry: string | null;
  capabilities: string[];
  is_real_estate: boolean;
  source: "explicit" | "deduced" | "unknown";
  reason: string;
  needs_setup: boolean;
  options: string[];
  /** Si alguien ya dio por terminada la bienvenida, la haya completado o no. */
  onboarding_completed: boolean;
}

/**
 * La única pregunta del alta.
 *
 * ## Por qué se pregunta, si el perfil se deducía solo
 *
 * Se deducía de los datos —CRM inmobiliario conectado, catálogo cargado— y para
 * los tenants que ya existen sigue siendo la respuesta: sus datos hablan solos y
 * nadie tiene que contestar nada.
 *
 * Lo que la deducción no puede hacer es **adivinar el día uno**. Un espacio
 * recién creado no tiene CRM ni fichas, así que da «no es inmobiliaria» justo
 * cuando más importa acertar: una inmobiliaria nueva vería una aplicación que no
 * habla de propiedades hasta conectar su sistema.
 *
 * Por eso esta pantalla aparece **solo cuando no hay nada que deducir**. Quien
 * ya tiene datos nunca la ve.
 */
export function IndustrySetup({ tenantName }: { tenantName: string }) {
  const [elegido, setElegido] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const guardar = useMutation({
    mutationFn: (industry: string) =>
      apiFetch<BusinessProfile>("/api/v1/me/profile", {
        method: "PUT",
        body: { industry },
      }),
    onSuccess: async () => {
      // El menú depende del rubro: Propiedades aparece o no según esto.
      await queryClient.invalidateQueries({ queryKey: keys.navigation });
      await queryClient.invalidateQueries({ queryKey: keys.profile });
    },
  });

  return (
    <main className="bg-background grid min-h-dvh place-items-center p-6">
      <div className="border-border bg-surface rounded-hero w-full max-w-lg border p-8">
        <img
          src="/clara.jpg"
          alt=""
          aria-hidden
          width={96}
          height={96}
          className="border-border size-12 rounded-full border object-cover"
        />
        <h1 className="mt-4 text-xl font-semibold">Hola, soy Clara</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Antes de empezar con {tenantName}, contame a qué se dedica. Con eso ajusto lo que ves:
          los campos de cada consulta, los ejemplos y qué módulos tienen sentido.
        </p>

        <div className="mt-6 space-y-3">
          {/* Inmobiliaria va aparte y primero: es el único rubro que cambia la
              aplicación, y separarlo evita que se pierda en una lista de nueve. */}
          <Opcion
            value="real_estate"
            label="Inmobiliaria"
            hint="Propiedades, visitas y búsquedas por zona, tipología y presupuesto"
            icon={Building2}
            elegido={elegido}
            onSelect={setElegido}
          />

          <div>
            <p className="text-muted-foreground mb-2 text-xs font-medium">Otro rubro</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {RUBROS.map((rubro) => (
                <Opcion
                  key={rubro.value}
                  value={rubro.value}
                  label={rubro.label}
                  elegido={elegido}
                  onSelect={setElegido}
                />
              ))}
            </div>
          </div>
        </div>

        <Button
          size="lg"
          className="mt-6 w-full"
          loading={guardar.isPending}
          disabled={!elegido}
          onClick={() => elegido && guardar.mutate(elegido)}
        >
          Empezar
        </Button>

        <p className="text-muted-foreground mt-3 text-center text-xs">
          Se puede cambiar después en Configuración.
        </p>

        {guardar.error && (
          <p role="alert" className="text-danger mt-3 text-center text-sm">
            {guardar.error.message}
          </p>
        )}
      </div>
    </main>
  );
}

function Opcion({
  value,
  label,
  hint,
  icon: Icon,
  elegido,
  onSelect,
}: {
  value: string;
  label: string;
  hint?: string;
  icon?: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  elegido: string | null;
  onSelect: (value: string) => void;
}) {
  const activo = elegido === value;
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      aria-pressed={activo}
      className={cn(
        "rounded-box flex w-full cursor-pointer items-center gap-3 border-[1.5px] p-3 text-left text-sm transition-colors",
        activo
          ? "border-primary bg-primary/10 text-primary-text font-medium"
          : "border-border hover:bg-surface-hover",
      )}
    >
      {Icon && <Icon aria-hidden className="size-4 shrink-0" />}
      <span className="min-w-0 flex-1">
        <span className="block">{label}</span>
        {hint && (
          <span className="text-muted-foreground block text-xs font-normal">{hint}</span>
        )}
      </span>
      {activo && <Check aria-hidden className="text-primary-text size-4 shrink-0" />}
    </button>
  );
}
